import React, { useMemo, useState } from 'react';
import { Clock, Play, Heart, Star, Filter, Headphones, Video, Search, Layers, Moon, Focus, HeartHandshake, Leaf } from 'lucide-react';
import { moodStates, categories, affirmationCategories } from '../data/mock';

const catIcon = {
  'All Meditations': Layers,
  'All Affirmations': Layers,
  'Mindfulness': Leaf,
  'Deep Sleep': Moon,
  'Stress & Anxiety': HeartHandshake,
  'Focus & Clarity': Focus,
  'Self-Compassion': HeartHandshake,
  'Morning Energy': Leaf,
  'Peace & Calm': Leaf,
  'Gratitude': Star,
  'Self-Love': HeartHandshake,
};

const SessionCard = ({ s, onPlay }) => (
  <div
    role="button"
    tabIndex={0}
    onClick={() => onPlay(s)}
    onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onPlay(s); } }}
    className="group cursor-pointer rounded-2xl overflow-hidden border border-emerald-100 bg-white hover:shadow-xl hover:shadow-emerald-900/10 hover:-translate-y-1 transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-emerald-500"
  >
    <div className="relative aspect-[16/10] bg-emerald-100">
      <img
        src={s.image}
        alt={s.title}
        onError={(e) => { e.currentTarget.style.display = 'none'; }}
        className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
      />
      <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
      <div className="absolute top-3 left-3 flex flex-wrap gap-2">
        <span className="rounded-full bg-white/90 backdrop-blur px-2.5 py-0.5 text-[10px] font-medium text-emerald-800">{s.langFlag} {s.language}</span>
        <span className="rounded-full bg-emerald-700/90 text-white px-2.5 py-0.5 text-[10px] font-medium">{s.category}</span>
        <span className="rounded-full bg-white/90 backdrop-blur px-2.5 py-0.5 text-[10px] font-medium text-emerald-800 inline-flex items-center gap-1">
          {s.format === 'Video' ? <Video className="w-3 h-3" /> : <Headphones className="w-3 h-3" />} {s.format}
        </span>
      </div>
      <span
        className="absolute inset-0 m-auto w-14 h-14 rounded-full bg-white/90 backdrop-blur flex items-center justify-center shadow-lg opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none"
        aria-hidden="true"
      >
        <Play className="w-5 h-5 fill-emerald-700 text-emerald-700 ml-0.5" />
      </span>
    </div>
    <div className="p-4">
      <h3 className="font-serif text-lg leading-tight text-emerald-950 line-clamp-2">{s.title}</h3>
      <p className="mt-1.5 text-sm text-emerald-900/70 line-clamp-2">{s.description}</p>
      <div className="mt-3 rounded-lg bg-emerald-50 border border-emerald-100 p-2.5 text-xs italic text-emerald-800/90">
        <span className="inline-flex items-center gap-1 not-italic font-medium text-emerald-700 mr-1"><Leaf className="w-3 h-3" /></span>
        “{s.note.slice(0, 110)}{s.note.length > 110 ? '…' : ''}”
      </div>
      <div className="mt-3 flex items-center justify-between text-xs text-emerald-800">
        <span className="inline-flex items-center gap-1"><Clock className="w-3.5 h-3.5" /> {s.duration} min</span>
        <span className="inline-flex items-center gap-1"><Play className="w-3.5 h-3.5" /> {s.plays.toLocaleString()}</span>
        <span className="inline-flex items-center gap-1"><Heart className="w-3.5 h-3.5" /> {s.likes}</span>
        <span className="inline-flex items-center gap-1"><Star className="w-3.5 h-3.5 fill-amber-500 text-amber-500" /> {s.rating.toFixed(1)}</span>
      </div>
    </div>
  </div>
);

const MeditationLibrary = ({ onPlay, meditations = [], affirmations = [] }) => {
  const [tab, setTab] = useState('meditations');
  const [language, setLanguage] = useState('All');
  const [format, setFormat] = useState('All');
  const [duration, setDuration] = useState('Any');
  const [category, setCategory] = useState(tab === 'meditations' ? 'All Meditations' : 'All Affirmations');
  const [mood, setMood] = useState('All States of Mind');
  const [search, setSearch] = useState('');

  const allMeds = meditations;
  const allAffs = affirmations;
  const activeList = tab === 'meditations' ? allMeds : allAffs;
  const activeCats = tab === 'meditations' ? categories : affirmationCategories;

  const filtered = useMemo(() => {
    return activeList.filter((s) => {
      if (language !== 'All' && s.language !== language) return false;
      if (format !== 'All' && s.format !== format) return false;
      if (duration === '<5' && s.duration >= 5) return false;
      if (duration === '5-15' && (s.duration < 5 || s.duration > 15)) return false;
      if (duration === '15+' && s.duration <= 15) return false;
      const catAll = category.startsWith('All');
      if (!catAll && s.category !== category) return false;
      if (search && !s.title.toLowerCase().includes(search.toLowerCase()) && !s.description.toLowerCase().includes(search.toLowerCase())) return false;
      return true;
    });
  }, [activeList, language, format, duration, category, search]);

  const switchTab = (t) => {
    setTab(t);
    setCategory(t === 'meditations' ? 'All Meditations' : 'All Affirmations');
  };

  return (
    <section id="meditations" className="scroll-mt-24 py-12 sm:py-20">
      <div className="max-w-7xl mx-auto px-4">
        <div className="flex flex-col items-start gap-3 mb-6">
          <div className="inline-flex items-center gap-2 rounded-full border border-emerald-200 bg-white px-3 py-1 text-xs text-emerald-700">
            <Filter className="w-3.5 h-3.5" /> How are you feeling right now?
          </div>
          <div className="flex flex-wrap gap-2">
            {moodStates.map((m) => (
              <button
                key={m}
                onClick={() => setMood(m)}
                className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${mood === m ? 'bg-emerald-700 text-white border-emerald-700' : 'bg-white text-emerald-800 border-emerald-200 hover:bg-emerald-50'}`}
              >
                {m}
              </button>
            ))}
          </div>
        </div>

        <div id="affirmations" className="scroll-mt-24 flex items-center gap-2 mb-6 p-1 rounded-full bg-emerald-50 border border-emerald-100 w-fit">
          <button
            onClick={() => switchTab('meditations')}
            className={`inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm transition-colors ${tab === 'meditations' ? 'bg-emerald-700 text-white shadow-sm' : 'text-emerald-800 hover:bg-white'}`}
          >
            <Leaf className="w-4 h-4" /> Guided Meditations ({allMeds.length})
          </button>
          <button
            onClick={() => switchTab('affirmations')}
            className={`inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm transition-colors ${tab === 'affirmations' ? 'bg-emerald-700 text-white shadow-sm' : 'text-emerald-800 hover:bg-white'}`}
          >
            <HeartHandshake className="w-4 h-4" /> Daily Affirmations ({allAffs.length})
          </button>
        </div>

        <div className="flex flex-col gap-1 mb-6">
          <span className="text-xs uppercase tracking-widest text-emerald-700">Curated Sessions</span>
          <h2 className="font-serif text-3xl sm:text-4xl md:text-5xl text-emerald-950">
            {tab === 'meditations' ? 'Meditation Library' : 'Affirmation Library'}
          </h2>
        </div>

        <div className="flex flex-wrap items-center gap-2 mb-4">
          {[['All', 'All Languages'], ['English', '🇺🇸 English'], ['Hindi', '🇮🇳 हिंदी (Hindi)']].map(([val, label]) => (
            <button key={val} onClick={() => setLanguage(val)} className={`text-xs px-3 py-1.5 rounded-full border ${language === val ? 'bg-emerald-700 text-white border-emerald-700' : 'bg-white text-emerald-800 border-emerald-200 hover:bg-emerald-50'}`}>{label}</button>
          ))}
          <span className="w-px h-5 bg-emerald-200 mx-1 hidden sm:block" />
          {[['All', 'All Formats'], ['Audio', 'Audio'], ['Video', 'Video']].map(([val, label]) => (
            <button key={val} onClick={() => setFormat(val)} className={`text-xs px-3 py-1.5 rounded-full border ${format === val ? 'bg-emerald-700 text-white border-emerald-700' : 'bg-white text-emerald-800 border-emerald-200 hover:bg-emerald-50'}`}>{label === 'Audio' ? '🎧 Audio' : label === 'Video' ? '🎬 Video' : label}</button>
          ))}
          <span className="w-px h-5 bg-emerald-200 mx-1 hidden sm:block" />
          <select value={duration} onChange={(e) => setDuration(e.target.value)} className="text-xs px-3 py-1.5 rounded-full border bg-white text-emerald-800 border-emerald-200 hover:bg-emerald-50 cursor-pointer">
            <option value="Any">Any Duration</option>
            <option value="<5">&lt; 5 Minutes</option>
            <option value="5-15">5 - 15 Minutes</option>
            <option value="15+">15+ Minutes</option>
          </select>
          <div className="ml-auto relative w-full sm:w-64">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-emerald-600" />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search sessions…"
              className="w-full pl-9 pr-3 py-2 rounded-full border border-emerald-200 bg-white text-sm text-emerald-900 placeholder-emerald-400 focus:outline-none focus:ring-2 focus:ring-emerald-300"
            />
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-2 mb-8">
          {activeCats.map((c) => {
            const Icon = catIcon[c] || Layers;
            const active = category === c;
            return (
              <button key={c} onClick={() => setCategory(c)} className={`inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full border transition-colors ${active ? 'bg-emerald-700 text-white border-emerald-700' : 'bg-white text-emerald-800 border-emerald-200 hover:bg-emerald-50'}`}>
                <Icon className="w-3.5 h-3.5" /> {c}
              </button>
            );
          })}
        </div>

        {filtered.length === 0 ? (
          <div className="rounded-2xl border border-dashed border-emerald-200 bg-emerald-50/40 p-10 text-center">
            <h3 className="font-serif text-2xl text-emerald-900">No Sessions Found</h3>
            <p className="text-sm text-emerald-800/80 mt-1">Try adjusting your language, category, format, or search keywords.</p>
            <button
              onClick={() => { setLanguage('All'); setFormat('All'); setDuration('Any'); setCategory(tab === 'meditations' ? 'All Meditations' : 'All Affirmations'); setSearch(''); }}
              className="mt-4 inline-flex items-center gap-2 px-4 py-2 rounded-full bg-emerald-700 text-white text-sm hover:bg-emerald-800"
            >
              Reset Filters
            </button>
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {filtered.map((s) => <SessionCard key={s.id} s={s} onPlay={onPlay} />)}
          </div>
        )}
      </div>
    </section>
  );
};

export default MeditationLibrary;
